/**
 * Утилиты для управления редиректами после авторизации
 * 
 * Использует гибридный подход:
 * - Query параметр `returnTo` (основной, прозрачный)
 * - sessionStorage (backup при обновлении страницы)
 * - Валидация от open redirect атак
 */

const STORAGE_KEY = 'auth_redirect'

/**
 * Проверяет валидность пути для редиректа
 * Защищает от open redirect атак
 * 
 * @param path - Путь для проверки
 * @returns true если путь безопасен
 */
function isValidReturnPath(path: string): boolean {
  if (!path) return false
  
  // Разрешаем только относительные пути, начинающиеся с /
  // Запрещаем // (схема-относительные URL)
  if (!path.startsWith('/') || path.startsWith('//')) {
    return false
  }

  try {
    // Проверяем что это валидный путь в нашем домене
    const url = new URL(path, window.location.origin)
    return url.origin === window.location.origin
  } catch {
    // Если не удалось распарсить как URL, считаем небезопасным
    return false
  }
}

/**
 * Сохраняет путь возврата в sessionStorage (backup механизм)
 * 
 * @param path - Путь для сохранения
 */
function saveReturnPath(path: string): void {
  if (isValidReturnPath(path)) {
    try {
      sessionStorage.setItem(STORAGE_KEY, path)
    } catch (error) {
      // Игнорируем ошибки (incognito mode, quota exceeded)
      console.warn('Failed to save return path to sessionStorage:', error)
    }
  }
}

/**
 * Извлекает сохранённый путь из sessionStorage и очищает его
 * 
 * @returns Сохранённый путь или null
 */
function getStoredReturnPath(): string | null {
  try {
    const path = sessionStorage.getItem(STORAGE_KEY)
    sessionStorage.removeItem(STORAGE_KEY)
    return path
  } catch (error) {
    // Игнорируем ошибки доступа к storage
    console.warn('Failed to get return path from sessionStorage:', error)
    return null
  }
}

/**
 * Создаёт URL для auth-страницы с сохранением текущего пути для возврата
 * 
 * @param authPath - Путь страницы авторизации (/login, /register, /forgot-password)
 * @param currentPath - Текущий путь пользователя (pathname + search + hash)
 * @param existingReturnTo - Уже существующий returnTo (для передачи между auth-формами)
 * @returns Строка пути с query параметром returnTo
 * 
 * @example
 * // Пользователь на /movies нажал "Sign In"
 * buildAuthUrl('/login', '/movies') // '/login?returnTo=%2Fmovies'
 * 
 * // С формы логина переход на регистрацию (передаём returnTo дальше)
 * buildAuthUrl('/register', '/login', '/movies') // '/register?returnTo=%2Fmovies'
 */
export function buildAuthUrl(
  authPath: string,
  currentPath: string,
  existingReturnTo?: string | null
): string {
  // Используем existingReturnTo если есть, иначе текущий путь
  const returnTo = existingReturnTo || currentPath

  // Валидация и проверка на auth-страницы
  if (!isValidReturnPath(returnTo) || isAuthPage(returnTo)) {
    return authPath
  }

  // Сохраняем в sessionStorage как backup
  saveReturnPath(returnTo)

  const params = new URLSearchParams()
  params.set('returnTo', returnTo)
  
  return `${authPath}?${params.toString()}`
}

/**
 * Извлекает returnTo из текущего URL
 * 
 * @param search - Строка query параметров из location.search
 * @returns URL для возврата или null
 * 
 * @example
 * getReturnToFromUrl('?returnTo=%2Fmovies') // '/movies'
 * getReturnToFromUrl('') // null
 */
export function getReturnToFromUrl(search: string): string | null {
  const params = new URLSearchParams(search)
  return params.get('returnTo')
}

/**
 * Определяет финальный URL для редиректа после авторизации
 * 
 * Использует приоритет:
 * 1. Query параметр returnTo
 * 2. sessionStorage backup
 * 3. fallbackPath
 * 
 * @param returnTo - Сохранённый URL из query параметра
 * @param fallbackPath - Путь по умолчанию (обычно '/')
 * @returns URL для редиректа
 * 
 * @example
 * getRedirectPath('/movies', '/') // '/movies'
 * getRedirectPath(null, '/') // Попытка восстановить из storage, иначе '/'
 * getRedirectPath('/login', '/') // '/' (защита от редиректа на auth-страницу)
 */
export function getRedirectPath(
  returnTo: string | null,
  fallbackPath: string = '/'
): string {
  // Приоритет 1: Query параметр
  // Приоритет 2: sessionStorage backup
  // Приоритет 3: fallback
  const path = returnTo || getStoredReturnPath() || fallbackPath

  // Валидация и проверка на auth-страницы
  if (!isValidReturnPath(path) || isAuthPage(path)) {
    return fallbackPath
  }

  return path
}

/**
 * Удаляет сохранённый путь возврата из sessionStorage
 */
export function deleteReturnPath(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch (error) {
    // Игнорируем ошибки доступа к storage
    console.warn('Failed to delete return path from sessionStorage:', error)
  }
}

/**
 * Проверяет, является ли путь страницей авторизации
 * 
 * @param path - Путь для проверки
 * @returns true если это страница авторизации
 */
function isAuthPage(path: string): boolean {
  const authPaths = ['/login', '/register', '/forgot-password']
  return authPaths.some(authPath => path.startsWith(authPath))
}
